import { useEffect, useState, Suspense, lazy } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import FirstAid from "@/components/tex/FirstAid";
import Ghs from "@/components/tex/Ghs";
import Ppe from "@/components/tex/Ppe";
import Spill from "@/components/tex/Spill";
import Compat from "@/components/tex/Compat";
import Sds from "@/components/tex/Sds";
import Coa from "@/components/tex/Coa";
import Standards from "@/components/tex/Standards";
import ToolPicker from "@/components/tex/ToolPicker";
import PageHero from "@/components/tex/PageHero";
import { staggerContainer, staggerItem } from "@/components/tex/PageTransition";

const Molecule3D = lazy(() => import("@/components/tex/Molecule3D"));

export default function ToolsPage() {
  const { company, chemicals, activeChem, selectChem, openQuote } = useOutletContext();
  const active = chemicals.find((c) => c.key === activeChem);
  const [activeTool, setActiveTool] = useState(null);
  const show = (id) => !activeTool || activeTool === id;

  useEffect(() => {
    document.title = `Tools — ${company?.name || "Texcheme"}`;
    window.scrollTo(0, 0);
  }, [company]);

  return (
    <>
      <PageHero
        eyebrow="Safety & compliance toolbelt"
        title="Eight shift-ready tools, one active chemical"
        lead="First-Aid emergency mode with a flush timer, PPE kit builder, spill planner, compatibility what-if checker, COA verifier with batch history, GHS label reader, and standards reference. Free every time, from Texcheme."
      />

      <div className="chem-strip" data-testid="chem-strip">
        <div className="chem-strip-inner wrap">
          <span className="chem-strip-label">Active substance</span>
          {chemicals.map((c) => (
            <button
              key={c.key}
              className={"chem-btn" + (c.key === activeChem ? " on" : "")}
              onClick={() => selectChem(c.key)}
              data-testid={`chem-${c.key}`}
            >
              <span className="chem-dot" style={{ background: c.color }} />
              {c.formula}
            </button>
          ))}
        </div>
      </div>

      <section className="tools-molecule-band" data-testid="tools-molecule-band">
        <motion.div
          className="wrap tools-molecule-grid"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <div>
            <motion.div className="eyebrow" variants={staggerItem}>
              Selected · {active?.name}
            </motion.div>
            <motion.h2 variants={staggerItem} style={{ marginTop: 4 }}>
              {active?.name}
            </motion.h2>
            <motion.p className="lead" variants={staggerItem} style={{ marginTop: 12 }}>
              {active?.grades || "Bulk-supply industrial grade"} · {active?.ghs}
            </motion.p>
          </div>
          <div className="diamond-stage">
            {active && (
              <motion.div
                className="molecule-stage"
                data-testid="hero-diamond"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                <Suspense fallback={<div className="molecule-fallback mono">Loading molecule…</div>}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={active.key}
                      style={{ width: "100%", height: "100%" }}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <Molecule3D chemKey={active.key} />
                    </motion.div>
                  </AnimatePresence>
                </Suspense>
                <div className="diamond-label">
                  <span className="mono" style={{ opacity: 0.75 }}>Molecular model ·</span>{" "}
                  <b>{active.formula}</b> · {active.name}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </section>

      <ToolPicker
        activeTool={activeTool}
        onSelect={setActiveTool}
        activeChem={activeChem}
        activeChemName={active?.name}
      />

      {show("first-aid") && <FirstAid chemKey={activeChem} chemName={active?.name} onRequestQuote={openQuote} />}
      {show("ghs") && <Ghs chemKey={activeChem} dark />}
      {show("ppe") && <Ppe chemKey={activeChem} chemName={active?.name} onRequestQuote={openQuote} />}
      {show("spill") && <Spill dark activeChem={activeChem} />}
      {show("compat") && <Compat onRequestQuote={openQuote} />}
      {show("sds") && <Sds dark onRequestQuote={openQuote} />}
      {show("coa") && <Coa onRequestQuote={openQuote} />}
      {show("standards") && <Standards dark chemName={active?.name} onRequestQuote={openQuote} />}

      <section className="panel cta-panel" data-testid="tools-cta-panel">
        <div className="wrap cta-panel-inner">
          <div>
            <h2>Need pricing for {active?.name || "any of our six chemicals"}?</h2>
            <p>Tell us your monthly tonnage and we&apos;ll get bulk-tanker pricing back the same day.</p>
          </div>
          <button
            className="btn btn-brand"
            onClick={() => openQuote("tools-cta")}
            data-testid="tools-cta-request-quote"
          >
            Request a quote
          </button>
        </div>
      </section>
    </>
  );
}
